import React, { useState, useRef, useEffect, memo } from 'react'
import { Box, styled, Typography, useTheme,InputBase,IconButton, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import SendIcon from '@mui/icons-material/Send';
import { socket } from '../../../config/SocketApi'
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import Attendee from './Attendee';
import Comment from './Comment';
import { useAuthContext } from '../../auth/hooks/useAuthContext';

// STYLED COMPONENTS - MOVED OUTSIDE to prevent recreation on every render
const ChatAreaContainer = styled(Box)(({ theme }) => ({
    flexDirection: 'column',
    height: '100%',
    display: 'flex',
    width: '100%',
    overflow: 'hidden',
}));

const ClassRoomAttendeeBox = styled(Box)(({ theme }) => ({
    height: '140px',
    minHeight: '100px',
    maxHeight: '140px',
    flexDirection: 'column',
    background: 'linear-gradient(145deg, rgba(26, 26, 46, 0.8) 0%, rgba(15, 15, 26, 0.9) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    overflowY: 'auto',
    overflowX: 'hidden',
    margin: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(10px)',
    flexShrink: 0,
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '3px',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.3)',
        },
    },
}));

const ClassRoomAtendeeHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.02)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backdropFilter: 'blur(5px)',
}));

const ClassRoomAttendees = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    padding: '6px',
    gap: '4px',
}));

const ClassRoomChatBox = styled(Box)(({ theme }) => ({
    flex: 1,
    background: 'linear-gradient(145deg, rgba(26, 26, 46, 0.8) 0%, rgba(15, 15, 26, 0.9) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    margin: '0 8px 8px 8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '3px',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.3)',
        },
    },
}));

const ClassRoomChatInputBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: '10px',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 8px 8px 8px',
    background: 'linear-gradient(145deg, rgba(26, 26, 46, 0.8) 0%, rgba(15, 15, 26, 0.9) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    flexShrink: 0,
}));

const ChatBox = styled(Box)(({ theme }) => ({
    padding: '10px 14px',
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '24px',
    maxWidth: '100%',
    wordBreak: 'normal',
    lineBreak: 'auto',
    position: 'relative',
    transition: 'all 0.2s ease',
    '&:focus-within': {
        border: '1px solid rgba(102, 126, 234, 0.5)',
        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
}));

const ButtonBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignContent: 'flex-end',
    justifyItems: 'flex-start',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
}));

const ChatArea = ({ room_id }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { user } = useAuthContext() || JSON.parse(localStorage.getItem("user"));
    const [sessionStarted, setSessionStarted] = useState(false)
    const [messages, setMessages] = useState([])
    const [lastMessage, setLastMessage] = useState('')
    const [privateMessage, setPrivateMessage] = useState();
    const [attendees, setAttendees] = useState([]);
    const sessionStartedRef = useRef(false);
    const chatAreaContainerRef = useRef();
    const [autoFocus, setAutoFocus] = useState(false)
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
    
    const InputBox = styled(InputBase)({
        flex: 1,
        color: theme.palette.text.default,
        backgroundColor: 'transparent',
        outline: 'none',
        border: 'none',
        wordBreak: 'normal',
        lineBreak: 'auto',
        fontSize: '16px', // Prevent iOS zoom
        minWidth: 0,
        '& input': {
            padding: 0,
        },
    })
    const messagesRef = useRef()
    useEffect(() => {
        socket.emit("send-attendees", {})
        if (!user) return
        socket.on("attendees", users => {
            setAttendees(users)
        })

        return () => {
            socket.off("attendees")
        }
    }, [user])

    const handAddMessage = (message, userName, userType, timeSent, fullName, imgPath, isPrivate = false) => {
        // Prevent duplicate messages by checking if message already exists
        setMessages(prev => {
            const exists = prev.some(m => 
                m.userName === userName && 
                m.timeSent === timeSent && 
                m.message === message
            );
            if (exists) {
                console.log('[ChatArea] Duplicate message prevented');
                return prev;
            }
            return [...prev, { message, userName, userType, timeSent, isPrivate, imgPath, fullName }];
        });
    }

    const handleLastMessage = (lastMessage) => {
        setLastMessage(lastMessage)
        // setEmojiPickerOpen(false)
    }

    useEffect(() => {
        socket.on("tutor-cam-enabled", (condition) => {
            console.log("the condition", condition, messagesRef.current, user)
            // messagesRef.current.style.height = (condition ? (user?.user_type !== 0 ? 'calc(100vh - 580px)' : 'calc(100vh - 480px)') : (user?.user_type !== 0 ? 'calc(100vh - 380px)' : 'calc(100vh - 280px)'))
            // chatAreaContainerRef.current.style.height = (condition ? (user?.user_type !== 0 ? 'calc(100vh - 360px)' : 'calc(100vh - 260px)') : (user?.user_type !== 0 ? 'calc(100vh - 160px)' : 'calc(100vh - 60px)'))
            setSessionStarted(condition)
            sessionStartedRef.current = condition;
        })
        socket.on("private-message", (message, userName, userType, timeSent, fullName, imgPath) => {
            handAddMessage(message, userName, userType, timeSent, fullName, imgPath, true)
        })
        socket.on("recieve-message", (message, userName, userType, timeSent, fullName, imgPath) => {
            handAddMessage(message, userName, userType, timeSent, fullName, imgPath)

        })

        return () => {
            socket.off("private-message");
            socket.off("tutor-cam-enabled");
            socket.off("recieve-message");
        }
    }, [])
    useEffect(() => {
        socket.on("got-connected-student-mic", (connected_student) => {
            const micConnectedAttendees = attendees.map(a => {
                return { ...a, micConnected: a?.micConnected || a?.user_id === connected_student?.user_id }
            })
            setAttendees(micConnectedAttendees)
        })
        return () => {
            socket.off("got-connected-student-mic")
        }
    }, [attendees])
    const handleSentPrivateMessage = (u_id, user_name) => {
        setPrivateMessage({ u_id, user_name })
    }
    useEffect(() => {
        messagesRef.current.scrollTo(0, messagesRef.current.scrollHeight);

        if(lastMessage !==""){
            setAutoFocus(true)
        }else{
            setAutoFocus(false)
        }
    }, [messages, lastMessage, sessionStarted])
    const handleSendMessage = () => {
        if (lastMessage !== "") {
            var date = new Date();
            const timeSent = date.getHours() + ":" + date.getMinutes() + ":" + date.getSeconds();
            if (privateMessage) {
                socket.emit("private-message", lastMessage, user.name, user.user_type, timeSent, privateMessage.u_id, user.full_name, user.img_path);
                setPrivateMessage()
            } else {
                socket.emit("public-message", lastMessage, user.name, user.user_type, timeSent, user.full_name, user.img_path);
            }
            handAddMessage(lastMessage, user.name, user.user_type, timeSent, user.full_name, user.img_path);
            handleLastMessage("")
        }
        setEmojiPickerOpen(false)
    }
    const handleKeypress = e => {
        if (e.code === 'Enter') {
            handleSendMessage();
        }
    };
    const inputRef = useRef();
    return (
        <ChatAreaContainer ref={chatAreaContainerRef}>
            <ClassRoomAttendeeBox>
                <ClassRoomAtendeeHeader>
                    <Typography>{t("attendeelist")} ({attendees.length})</Typography>
                    {user && user.user_type !== 0 && <ExitToAppIcon sx={{ color: theme.palette.primary.main }} />}
                </ClassRoomAtendeeHeader>
                <ClassRoomAttendees>
                    {attendees.map((attendee, index) => <Attendee key={attendee.socket_id || attendee.user_id || index} data={attendee} room_id={room_id} onaddPrivateMessage={(user_id, user_name) => handleSentPrivateMessage(user_id, user_name)} />)}
                </ClassRoomAttendees>
            </ClassRoomAttendeeBox>
            <ClassRoomChatBox ref={messagesRef}>
                <Typography sx={{ margin: '4px 24px 4px 24px', }}>{t("chatbox")} {messages.length !== 0 ? `(${messages.length})` : ''}</Typography>
                {messages.map((message, index) => (<Comment key={message.id || message.timestamp || index} data={message} user={user} />))}
            </ClassRoomChatBox>
            <ClassRoomChatInputBox>
                <ChatBox>
                    {privateMessage && <Chip sx={{ position: 'absolute', zIndex: 1000, top: '-25px', right: 0, paddingLeft: '10px' }} label={t('private_message_to') + ' ' + privateMessage.user_name} variant='filled' color='warning' onDelete={() => setPrivateMessage()} />}
                    <InputBox onClick={()=>setAutoFocus(true)} onKeyDown={(e) => handleKeypress(e)} dir={theme.direction} ref={inputRef} placeholder={t("sendmessage")} autoFocus={autoFocus} value={lastMessage} onChange={(e) => { setLastMessage(e.target.value) }}></InputBox>
                    {emojiPickerOpen && <Box sx={{ position: 'absolute', top: "-300px", right: "0px", zIndex: 1000 }}>
                        <EmojiPicker
                            width="100%"
                            height={300}
                            searchDisabled={true}
                            lazyLoadEmojis={true}
                            onEmojiClick={(e) => handleLastMessage(prev => prev + e.emoji)}
                            emojiStyle={EmojiStyle.NATIVE}
                        >
                        </EmojiPicker>
                    </Box>}
                    {user.user_type === 0 ? <ButtonBox>

                        <IconButton sx={{ margin: 0, padding: 0.3 }} onClick={() => setEmojiPickerOpen(prev => !prev)}><EmojiEmotionsIcon sx={{ width: 15, margin: 0 }} /></IconButton>
                        {/* <IconButton sx={{ margin: 0, padding: 0.3 }} onClick={() => handleRaiseHand()}><BackHandIcon sx={{ width: 15, margin: 0 }} /></IconButton> */}
                        <IconButton sx={{ margin: 0, padding: 0.3 }} onClick={() => handleSendMessage()}>
                            <SendIcon sx={{ width: 15, margin: 0 }} />
                        </IconButton>
                    </ButtonBox> :
                        <>
                            <IconButton onClick={() => setEmojiPickerOpen(prev => !prev)}><EmojiEmotionsIcon /></IconButton>
                            <IconButton onClick={() => handleSendMessage()}>
                                <SendIcon />
                            </IconButton>
                        </>
                    }
                </ChatBox>
                {/* <ChatInput onPrivateMessageSent={() => setPrivateMessage()} privateMessage={privateMessage} lastMessage={lastMessage} onAddComment={(message, userName, userType, timeSent, fullName, imgPath) => handAddMessage(message, userName, userType, timeSent, fullName, imgPath)} onChangeLastMessage={(lastMessage) => handleLastMessage(lastMessage)} /> */}
            </ClassRoomChatInputBox>
        </ChatAreaContainer>
    )
}

export default memo(ChatArea)